import { observable, computed, action, makeAutoObservable, runInAction } from 'mobx';
import i18n from '~/i18n';
import { Amplify, Auth, Hub } from 'aws-amplify';
import { configure as AxiosHooksConfigure } from 'axios-hooks';
import { Modal, message } from 'antd';
import api from '@lib/api';
import EstimationProgressSocketUtil from '@lib/socket';
import AmplifyConfig from '~/config/amplify';

import { IUserData } from '@interfaces/user.interface';
import { APIGetVideoDataWithChapters, Chapters } from '~/interfaces/pose.interface';
import { UploadingVideoData } from '~/lib/video-uploader';
import { getSelectedThumbnailItemUrl } from '~/utils';
// import { delay } from '~/utils';

export interface ProcessingVideoData {
  videoId: string;
  title: string;
  createdAt: string;
  updatedAt: string;
  thumbnailUrl: string;
  progress: number;
  chapters: Chapters;
  currentProcessingChapterIdx: number;
}

export interface IUserStore {
  authState: 'loading' | 'signIn' | 'signedIn';
  authTokenData: any;
  userState: boolean;
  userData: IUserData;
  gpuSocketConnected: boolean;
  currentlyProcessingVideoIds: string[];
  currentlyProcessingVideoDatas: Record<string, ProcessingVideoData>;
  completedProcessingVideoIds: string[];
  currentlyUploadingVideoDatas: Record<string, UploadingVideoData>;
  completedUploadingVideoIds: string[];
  idToken: string;

  initialize: () => void;
  auth: () => void;
  unauth: () => void;
  fetchUser: () => void;
  signOut: () => void;
  injectProcessingVideo: (videoId: string) => void;
  updateProgressProcessingVideo: (videoId: string, chapterIdx: number, progress: number) => void;
  completeProgressProcessingVideo: (videoId: string) => void;
  addUploadingVideo: (video: UploadingVideoData) => void;
  setUploadingVideo: (uid: string, payload: Partial<UploadingVideoData>) => void;
  removeUploadingVideo: (uid: string) => void;
  completeUploadingVideo: (uid: string) => void;
  ejectProcessingVideo: (videoId: string) => void;

  isLoading: boolean;
  isAuthenticated: boolean;
  isFetchedUser: boolean;
  isVerifiedCreator: boolean | null;
  name: string;
  avatarUrl: string;
  isPoseProcessing: boolean;
  currentProcessingVideoCount: number;
  isUploadingVideo: boolean;
  hasErrorUploadingVideo: boolean;
  currentUploadingVideoCount: number;
}

Amplify.configure(AmplifyConfig);

// const initialState = {
//   authState: 'loading',
//   userState: false,
//   userData: {
//     userId: '',
//     referenceId: '',
//     createdAt: '',
//     updatedAt: '',
//     mainChannel: '',
//     creator: null,
//     grant: null,
//     poseProcessingVideoIds: [],
//   },
//   gpuSocketConnected: false
//   currentlyProcessingVideoIds = []
//   currentlyProcessingVideoDatas = {}
//   authTokenData: {},
//   idToken: '',
// };

class UserStore {
  @observable
  authState: 'loading' | 'signIn' | 'signedIn' = 'loading';

  @observable
  userState = false;

  @observable
  userData: IUserData = {
    userId: '',
    referenceId: '',
    createdAt: '',
    updatedAt: '',
    mainChannel: '',
    creator: null,
    grant: null,
    poseProcessingVideoIds: [],
    name: null,
    avatarUrl: null,
  };

  gpuSocketConnected = false;

  @observable
  currentlyProcessingVideoIds: string[] = [];

  @observable
  currentlyProcessingVideoDatas: Record<string, ProcessingVideoData> = {};

  @observable
  completedProcessingVideoIds: string[] = [];

  @observable
  currentlyUploadingVideoDatas: Record<string, UploadingVideoData> = {};

  @observable
  completedUploadingVideoIds: string[] = [];

  @observable
  authTokenData: any = {};

  idToken = '';

  constructor() {
    makeAutoObservable(this);
  }

  @action
  async initialize() {
    Hub.listen('auth', async ({ payload: { event } }) => {
      switch (event) {
        case 'signIn':
          await this.auth();
          break;
        case 'signIn_failure':
          Modal.error({
            title: i18n.t('error.api.common.retrieve.server.title'),
            content: i18n.t('error.api.user.failed'),
            okText: i18n.t('a11y.ok'),
            onOk() {
              Auth.signOut();
            },
          });
          break;
        default:
      }
    });

    await Auth.currentAuthenticatedUser()
      .then(async () => await this.auth())
      .catch(() => this.signOut());
  }

  @action
  async auth() {
    try {
      const user = await Auth.currentAuthenticatedUser();
      const session = user.signInUserSession;

      api.defaults.headers.common.Authorization = `Bearer ${session.idToken.jwtToken}`;
      AxiosHooksConfigure({ axios: api });
      this.idToken = session.idToken.jwtToken;
      this.authTokenData = session.idToken.payload;
      await this.fetchUser();
      runInAction(() => {
        this.authState = 'signedIn';
      });
    } catch (e) {
      Modal.error({
        title: i18n.t('error.api.common.retrieve.server.title'),
        content: i18n.t('error.api.user.failed'),
        okText: i18n.t('a11y.ok'),
        onOk() {
          Auth.signOut();
        },
      });
      console.error('Amplify auth error!', e);
      this.unauth();
    }
  }

  @action
  unauth() {
    this.authState = 'signIn';
    this.authTokenData = {};
    this.userState = true;
  }

  @action
  async fetchUser() {
    try {
      const user = await api.get<IUserData>('/user', {
        params: {
          opt_fields: 'poseprocessingvideoids',
        },
      });

      // [TODO] remove channel apply process. todo agreement ui
      // if (!user.data.mainChannel) {
      //   await api.post('/channel', { name: this.authTokenData.name.substring(0, 13) + ' Studio' });
      //   await delay(1000);
      //   user = await api.get<IUserData>('/user', {
      //     params: {
      //       opt_fields: 'poseprocessingvideoids',
      //     },
      //   });
      // }

      this.userData = user.data;
      this.userState = true;

      if (user.data.poseProcessingVideoIds.length) {
        user.data.poseProcessingVideoIds.forEach((videoId) => this.injectProcessingVideo(videoId));
      }
    } catch (e) {
      Modal.error({
        title: i18n.t('error.api.common.retrieve.server.title'),
        content: i18n.t('error.api.user.failed'),
        okText: i18n.t('a11y.ok'),
        onOk() {
          Auth.signOut();
        },
      });
      this.unauth();
    }
  }

  @action
  signOut() {
    Auth.signOut();
    this.unauth();
  }

  @action
  async injectProcessingVideo(videoId: string) {
    if (this.currentlyProcessingVideoIds.includes(videoId)) return;
    this.currentlyProcessingVideoIds.push(videoId);

    try {
      const {
        data: { title, thumbnails, pose },
      } = await api.get<APIGetVideoDataWithChapters>(`/video/${videoId}?opt_fields=pose`);

      if (!this.gpuSocketConnected) EstimationProgressSocketUtil.connect();
      EstimationProgressSocketUtil.subscribeVideo(videoId);

      this.currentlyProcessingVideoDatas[videoId] = {
        videoId,
        title,
        createdAt: pose.createdAt,
        updatedAt: pose.updatedAt,
        thumbnailUrl: getSelectedThumbnailItemUrl(thumbnails) ?? '/assets/player/default-video.png',
        progress: 0,
        chapters: pose.chapters,
        currentProcessingChapterIdx: 0,
      };
    } catch (e) {
      message.error(`Failed to retrieve video data`);
    }
  }

  @action
  updateProgressProcessingVideo(videoId: string, chapterIdx: number, progress: number) {
    this.currentlyProcessingVideoDatas[videoId].currentProcessingChapterIdx = chapterIdx;
    this.currentlyProcessingVideoDatas[videoId].progress = progress;
  }

  @action
  completeProgressProcessingVideo(videoId: string) {
    this.completedProcessingVideoIds.push(videoId);
  }

  @action
  addUploadingVideo(video: UploadingVideoData) {
    this.currentlyUploadingVideoDatas[video.uid] = video;
  }

  @action
  setUploadingVideo(uid: string, payload: Partial<UploadingVideoData>) {
    if (this.currentlyUploadingVideoDatas[uid]) {
      Object.assign(this.currentlyUploadingVideoDatas[uid], payload);
    }
  }

  @action
  removeUploadingVideo(uid: string) {
    delete this.currentlyUploadingVideoDatas[uid];

    const completedIndex = this.completedUploadingVideoIds.findIndex((id) => id === uid);

    if (completedIndex) this.completedUploadingVideoIds.splice(completedIndex, 1);
  }

  @action
  completeUploadingVideo(uid: string) {
    this.completedUploadingVideoIds.push(uid);
  }

  @action
  ejectProcessingVideo(videoId: string) {
    const targetCurrIndex = this.currentlyProcessingVideoIds.indexOf(videoId);
    const targetCompIndex = this.completedProcessingVideoIds.indexOf(videoId);

    if (targetCurrIndex > -1) {
      this.currentlyProcessingVideoIds.splice(targetCurrIndex, 1);
      delete this.currentlyProcessingVideoDatas[videoId];
      this.completedProcessingVideoIds.splice(targetCompIndex, 1);
    }

    if (!this.currentlyProcessingVideoIds.length) {
      EstimationProgressSocketUtil.disconnect();
    }
  }

  @computed
  get isLoading() {
    return this.authState === 'loading' || (!this.userState && !this.userData);
  }

  @computed
  get isAuthenticated() {
    return this.authState === 'signedIn' && this.userState;
  }

  @computed
  get isFetchedUser() {
    return !!this.userData.userId;
  }

  @computed
  get isVerifiedCreator() {
    return this.userData.creator;
  }

  @computed
  get name() {
    return this.userData.name ?? this.authTokenData.name;
  }

  @computed
  get avatarUrl() {
    return (
      this.userData.avatarUrl ??
      this.authTokenData.picture ??
      '/assets/common/userpicture-default-md.png'
    );
  }

  @computed
  get isPoseProcessing() {
    return this.currentlyProcessingVideoIds.length - this.completedProcessingVideoIds.length > 0;
  }

  @computed
  get currentProcessingVideoCount() {
    return this.currentlyProcessingVideoIds.length - this.completedProcessingVideoIds.length;
  }

  @computed
  get isUploadingVideo() {
    const { currentlyUploadingVideoDatas } = this;

    return (
      Object.keys(currentlyUploadingVideoDatas).filter(
        (key) =>
          currentlyUploadingVideoDatas[key].state === 'presigning' ||
          currentlyUploadingVideoDatas[key].state === 'uploading',
      ).length > 0
    );
  }

  @computed
  get hasErrorUploadingVideo() {
    const { currentlyUploadingVideoDatas } = this;

    return (
      Object.keys(currentlyUploadingVideoDatas).filter(
        (key) => currentlyUploadingVideoDatas[key].state === 'error',
      ).length > 0
    );
  }

  @computed
  get currentUploadingVideoCount() {
    return Object.keys(this.currentlyUploadingVideoDatas).length;
  }
}

export default new UserStore();
